'use client';

import { Link } from '@/i18n/navigation';
import { XCircle } from 'lucide-react';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  // Sanitizar mensaje de error (máximo 200 caracteres, fallback a mensaje genérico)
  const errorMessage = error.message
    ? error.message.slice(0, 200) + (error.message.length > 200 ? '...' : '')
    : 'An unexpected error occurred';

  return (
    <main className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="relative max-w-md w-full">
        {/* Glass card con glow effect */}
        <div className="absolute inset-0 bg-white/5 backdrop-blur-xl rounded-2xl border border-gray-700/50" />
        <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-b from-white/10 to-transparent" />
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 via-pink-500/10 to-purple-500/20 blur-xl opacity-50" />

        <div className="relative p-4 sm:p-6 lg:p-8 text-center">
          {/* Ícono con gradiente */}
          <XCircle className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 text-pink-500" />

          {/* Título */}
          <h1 className="text-2xl font-bold text-white mb-3">Something went wrong</h1>

          {/* Descripción (mensaje de error sanitizado) */}
          <p className="text-gray-400 mb-8 break-words">
            {errorMessage}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={reset}
              className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-pink-500/25"
            >
              Try Again
            </button>
            <Link
              href="/"
              className="inline-block px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all duration-200 border border-gray-700/50"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
